import type { FinePreviewRow } from './types.ts'

export const MAX_FINE_IMPORT_LINES = 500
export const MAX_FINE_IMPORT_CHARS = 50_000
export const DAY_MS = 86_400_000

export function todayDateInput(now = new Date()): string {
  const year = now.getFullYear()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export function dateInputToUtcMs(value: string): number | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  if (!match) return null

  const year = Number(match[1])
  const month = Number(match[2])
  const day = Number(match[3])
  const timestamp = Date.UTC(year, month - 1, day)
  const date = new Date(timestamp)
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return null
  }
  return timestamp
}

export function validatePreviewRow(row: FinePreviewRow): string[] {
  const errors: string[] = []
  if (!Number.isInteger(row.amountCents) || (row.amountCents ?? 0) <= 0) {
    errors.push('Amount must be greater than zero')
  }
  if (!row.description.trim()) errors.push('Description is required')
  return errors
}

function parseAmount(description: string): { amountCents: number | null; error?: string } {
  const match = /\$\s*(-?\d+(?:\.\d+)?)/.exec(description)
  if (!match) return { amountCents: 200 }

  const decimal = match[1]
  const fraction = decimal.split('.')[1]
  if (fraction && fraction.length > 2) {
    return { amountCents: null, error: 'Amount may have at most two decimal places' }
  }

  const amount = Number(decimal)
  const amountCents = Math.round(amount * 100)
  if (!Number.isFinite(amount) || amountCents <= 0) {
    return { amountCents: null, error: 'Amount must be greater than zero' }
  }
  return { amountCents }
}

function emptyRow(lineNumber: number): FinePreviewRow {
  return {
    id: `line-${lineNumber}`,
    lineNumber,
    sourceName: '',
    description: '',
    amountCents: null,
    type: 'Fine',
    matchedUserId: null,
    matchedUserName: null,
    confidence: 0,
    matchStatus: 'unmatched',
    manuallyMatched: false,
    hasDuplicate: false,
    duplicateWarning: null,
    skipRow: false,
    skipReason: null,
    allowDuplicate: false,
    validationErrors: [],
  }
}

export function parseFineText(text: string): FinePreviewRow[] {
  if (text.length > MAX_FINE_IMPORT_CHARS) {
    throw new Error(`Paste must be ${MAX_FINE_IMPORT_CHARS.toLocaleString()} characters or fewer`)
  }

  const lines = text
    .split(/\r?\n/)
    .map((line, index) => ({ line: line.trim(), lineNumber: index + 1 }))
    .filter(({ line }) => line.length > 0)

  if (lines.length === 0) throw new Error('No fines to import')
  if (lines.length > MAX_FINE_IMPORT_LINES) {
    throw new Error(`Import supports at most ${MAX_FINE_IMPORT_LINES} non-empty lines`)
  }

  return lines.map(({ line, lineNumber }) => {
    const row = emptyRow(lineNumber)
    const separator = line.indexOf('-')
    if (separator < 0) {
      return {
        ...row,
        sourceName: line,
        validationErrors: ['Missing "-" separator; expected "Name - description"'],
      }
    }

    const sourceName = line.slice(0, separator).trim()
    const description = line.slice(separator + 1).trim()
    const amount = parseAmount(description)
    const rowWithValues: FinePreviewRow = {
      ...row,
      sourceName,
      description,
      amountCents: amount.amountCents,
      type: description.toLocaleLowerCase().includes('registration') ? 'Registration' : 'Fine',
    }
    const validationErrors = validatePreviewRow(rowWithValues)
    if (!sourceName) validationErrors.unshift('Name is required')
    if (amount.error && !validationErrors.includes(amount.error)) validationErrors.push(amount.error)
    return { ...rowWithValues, validationErrors }
  })
}
